import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { workspaceStore } from "../lib/workspaceStore";
import { useDeviceProfile } from "../hooks/useDeviceProfile";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";

export default function StudioPage() {
  const device = useDeviceProfile();
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [brandDna, setBrandDna] = useState(null);
  const [useWorkspaceContext, setUseWorkspaceContext] = useState(true);
  const [customDirection, setCustomDirection] = useState("");

  const [people, setPeople] = useState([]);
  const [packs, setPacks] = useState([]);
  const [packKey, setPackKey] = useState("meta_ad_pack");

  const [product, setProduct] = useState("");
  const [goal, setGoal] = useState("sales");
  const [notes, setNotes] = useState("");

  const [spokespersonMode, setSpokespersonMode] = useState(false);
  const [studioMode, setStudioMode] = useState("generate");
  const [presenterKey, setPresenterKey] = useState("cole_creative");
  const [presenterStyle, setPresenterStyle] = useState("");
  const [presenterKeys, setPresenterKeys] = useState(["cole_creative"]);
  const [variantsPerPresenter, setVariantsPerPresenter] = useState(2);

  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const [genId, setGenId] = useState(null);
  const [output, setOutput] = useState(null);

  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const captureUrlRef = useRef("");
  const [cameraStatus, setCameraStatus] = useState("idle");
  const [cameraErr, setCameraErr] = useState(null);
  const [captureConsent, setCaptureConsent] = useState(false);
  const [performerName, setPerformerName] = useState("");
  const [captureStatus, setCaptureStatus] = useState("idle");
  const [captureUrl, setCaptureUrl] = useState("");
  const [captureBlob, setCaptureBlob] = useState(null);
  const [cameraQuality, setCameraQuality] = useState(null);
  const [cameraClipStatus, setCameraClipStatus] = useState("idle");
  const [cameraClip, setCameraClip] = useState(null);
  const [cameraClipUrl, setCameraClipUrl] = useState("");
  const [cameraPermissionPending, setCameraPermissionPending] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment");
  const [cameraDevices, setCameraDevices] = useState([]);
  const [cameraDeviceId, setCameraDeviceId] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef(null);
  const audioFrameRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    try {
      const handoff = JSON.parse(window.localStorage.getItem("omnimark_dropshipping_handoff") || "null");
      if (!handoff?.product) return;
      setProduct(handoff.product);
      setNotes(handoff.brief || handoff.angle || "");
      setCustomDirection([
        handoff.angle ? `Primary product angle: ${handoff.angle}` : null,
        handoff.theme ? `Landing-page theme: ${handoff.theme}` : null,
        handoff.source_url ? `Research source: ${handoff.source_url}` : null,
      ].filter(Boolean).join("\n"));
    } catch {
      // Ignore malformed browser state and keep Studio usable.
    }
  }, []);

  const ready = useMemo(() => {
    return product.trim().length >= 3 || Boolean(useWorkspaceContext && (brandDna?.offer || workspace?.name));
  }, [brandDna?.offer, product, useWorkspaceContext, workspace?.name]);
  const studioDeviceCopy = device.supportsCameraStudio
    ? `Optimized for ${device.label}. Use the rear camera when recording products and keep the phone/tablet vertical for UGC clips.`
    : `Detected ${device.label}. This layout is optimized for planning, scripting, and previewing. Use a phone or tablet for direct camera capture.`;

  const selectedPack = useMemo(
    () => packs.find((p) => p.key === packKey) || null,
    [packs, packKey]
  );

  const presenters = useMemo(
    () => people.filter((p) => p.can_present),
    [people]
  );

  const selectedAvatar = useMemo(() => {
    if (!workspace || typeof window === "undefined") return null;
    const keys = [
      `selected_avatar_${workspace.id}`,
      `omnimark_selected_avatar_${workspace.id}`,
      "selected_avatar",
      "omnimark_selected_avatar",
    ];
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.name) return parsed;
      } catch (e) {
        // Ignore legacy non-JSON values.
      }
    }
    return null;
  }, [workspace]);

  const workspaceBrief = useMemo(() => {
    if (!workspace) return "";
    const brand = brandDna || {};
    const lines = [
      `Workspace: ${workspace.name}`,
      brand.business_type ? `Business type: ${brand.business_type}` : null,
      brand.offer ? `Offer: ${brand.offer}` : null,
      brand.website_url ? `Website/domain: ${brand.website_url}` : null,
      brand.audience ? `Audience: ${brand.audience}` : null,
      brand.tone ? `Tone: ${brand.tone}` : null,
      brand.differentiator ? `Differentiator: ${brand.differentiator}` : null,
      brand.compliance_notes ? `Compliance notes: ${brand.compliance_notes}` : null,
      selectedAvatar?.name ? `Selected avatar: ${selectedAvatar.name}` : null,
    ].filter(Boolean);
    return lines.join("\n");
  }, [brandDna, selectedAvatar?.name, workspace]);

  const finalStudioNotes = useMemo(() => {
    const sections = [];
    if (useWorkspaceContext && workspaceBrief) sections.push(`WORKSPACE INTELLIGENCE\n${workspaceBrief}`);
    if (customDirection.trim()) sections.push(`CUSTOM DIRECTION\n${customDirection.trim()}`);
    if (notes.trim()) sections.push(`USER NOTES\n${notes.trim()}`);
    return sections.join("\n\n");
  }, [customDirection, notes, useWorkspaceContext, workspaceBrief]);

  // Calculate estimate whenever relevant inputs change
  useEffect(() => {
    if (!selectedPack) return;
    
    const fetchEstimate = async () => {
      setEstimating(true);
      try {
        const res = await api.post("/api/credits/estimate", {
          sequence: selectedPack.sequence,
          spokesperson_mode: spokespersonMode,
          presenter_keys: spokespersonMode ? presenterKeys : null,
          variants_per_presenter: spokespersonMode ? variantsPerPresenter : 0,
        });
        setEstimate(res.data);
      } catch (e) {
        setEstimate(null);
      } finally {
        setEstimating(false);
      }
    };
    
    fetchEstimate();
  }, [selectedPack, spokespersonMode, presenterKeys, variantsPerPresenter]);

  useEffect(() => {
    loadEverything();
  }, []);

  useEffect(() => {
    captureUrlRef.current = captureUrl;
  }, [captureUrl]);

  useEffect(() => {
    return () => {
      recorderRef.current?.stop?.();
      streamRef.current?.getTracks()?.forEach((track) => track.stop());
      if (captureUrlRef.current) URL.revokeObjectURL(captureUrlRef.current);
      if (cameraClipUrl) URL.revokeObjectURL(cameraClipUrl);
    };
  }, [cameraClipUrl]);

  // Save workspace selection to localStorage for other pages
  useEffect(() => {
    if (workspace) {
      workspaceStore.setSelected(workspace);
    }
  }, [workspace]);

  useEffect(() => {
    if (!workspace?.id) {
      setBrandDna(null);
      return;
    }
    let alive = true;
    api.get(`/api/workspaces/${workspace.id}/brand-dna`)
      .then((res) => {
        if (alive) setBrandDna(res.data || null);
      })
      .catch(() => {
        if (alive) setBrandDna(null);
      });
    return () => {
      alive = false;
    };
  }, [workspace?.id]);

  async function loadEverything() {
    setErr(null);
    try {
      const [ppl, pck, ws] = await Promise.all([
        api.get("/api/ai/people"),
        api.get("/api/ai/packs"),
        api.get("/api/workspaces"),
      ]);
      setPeople(ppl.data.items || []);
      setPacks(pck.data.items || []);
      setWorkspaces(ws.data.items || []);

      // Try to restore previously selected workspace, or pick first
      const stored = workspaceStore.getSelected();
      const wsList = ws.data.items || [];
      if (stored && wsList.find((w) => w.id === stored.id)) {
        setWorkspace(stored);
      } else if (wsList.length > 0) {
        setWorkspace(wsList[0]);
      }

      if ((pck.data.items || []).length > 0) {
        setPackKey(pck.data.items[0].key);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load AI Studio data.");
    }
  }

  async function runPack() {
    if (!workspace) return;
    setErr(null);
    setRunning(true);
    setGenId(null);
    setOutput(null);

    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/studio/run-pack`, {
        pack_key: packKey,
        product_or_service: product || brandDna?.offer || workspace.name,
        goal,
        notes: finalStudioNotes,
        spokesperson_mode: spokespersonMode,
        presenter_keys: spokespersonMode ? presenterKeys : null,
        variants_per_presenter: spokespersonMode ? variantsPerPresenter : 0,
        presenter_style: spokespersonMode ? (presenterStyle || null) : null,
        selected_avatar: selectedAvatar,
      });

      setGenId(res.data.id);
      setOutput(res.data.output);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      if (status === 402) setErr(detail || "Not enough credits. Upgrade or buy more credits.");
      else setErr(detail || "Failed to run pack. Check backend logs.");
    } finally {
      setRunning(false);
    }
  }

  function stopAudioMeter() {
    if (audioFrameRef.current) cancelAnimationFrame(audioFrameRef.current);
    audioFrameRef.current = null;
    if (audioContextRef.current) audioContextRef.current.close?.();
    audioContextRef.current = null;
    setAudioLevel(0);
  }

  function startAudioMeter(stream) {
    stopAudioMeter();
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext || !stream?.getAudioTracks?.().length) return;
    try {
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.frequencyBinCount);
      const measure = () => {
        analyser.getByteTimeDomainData(samples);
        const average = samples.reduce((sum, value) => sum + Math.abs(value - 128), 0) / samples.length;
        setAudioLevel(Math.min(100, Math.round((average / 40) * 100)));
        audioFrameRef.current = requestAnimationFrame(measure);
      };
      audioContextRef.current = context;
      measure();
    } catch {
      // The capture itself remains usable if a device/browser declines analysis.
    }
  }

  async function startMobileStudioCamera(options = {}) {
    setCameraErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus("unsupported");
      setCameraErr("Camera and microphone access is not supported in this browser.");
      return;
    }
    try {
      stopMobileStudioCamera();
      const nextFacing = options.facingMode || cameraFacing;
      const nextDeviceId = options.deviceId === undefined ? cameraDeviceId : options.deviceId;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...(nextDeviceId ? { deviceId: { exact: nextDeviceId } } : { facingMode: { ideal: nextFacing } }),
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      startAudioMeter(stream);
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      setCameraDevices(cameras);
      const selectedId = stream.getVideoTracks?.()[0]?.getSettings?.().deviceId || nextDeviceId || "";
      setCameraDeviceId(selectedId);
      setCameraFacing(nextFacing);
      const settings = stream.getVideoTracks?.()[0]?.getSettings?.() || {};
      const width = Number(settings.width || 0);
      const height = Number(settings.height || 0);
      const frameRate = Number(settings.frameRate || 0);
      const recommendations = [
        width && height && Math.min(width, height) < 720 ? "Use a camera that can record at least 720p for final delivery." : null,
        frameRate && frameRate < 24 ? "Use 24 fps or higher when possible to avoid choppy motion." : null,
        "Use even front light, clean the lens, and keep the subject/product in focus before recording.",
        "Get permission from every recognizable person and avoid copyrighted music in a take.",
      ].filter(Boolean);
      setCameraQuality({ width, height, frameRate, recommendations });
      setCameraStatus("ready");
    } catch (e) {
      setCameraStatus("blocked");
      setCameraErr("Camera or microphone permission was blocked. Allow access to record product videos.");
    }
  }

  function stopMobileStudioCamera() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = null;
    setCountdown(0);
    stopAudioMeter();
    streamRef.current?.getTracks()?.forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraStatus("idle");
    setCameraQuality(null);
  }

  function requestStudioCamera() {
    // Make the site and purpose visible before the browser/OS-owned prompt.
    // The native prompt's title cannot be renamed by a web page.
    setCameraPermissionPending(true);
  }

  function startCameraClipCapture() {
    if (!streamRef.current || !window.MediaRecorder) {
      setCameraErr("Allow camera + mic first. Use a current Chrome, Edge, or Safari browser to record a clip.");
      return;
    }
    if (cameraClipUrl) URL.revokeObjectURL(cameraClipUrl);
    const chunks = [];
    const recorder = new MediaRecorder(streamRef.current, { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm" });
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType || "video/webm" });
      const url = URL.createObjectURL(blob);
      setCameraClip(blob);
      setCameraClipUrl(url);
      setCameraClipStatus("ready");
      recorderRef.current = null;
    };
    recorderRef.current = recorder;
    recorder.start(1000);
    setCameraClipStatus("recording");
    setCameraErr(null);
  }

  function startCameraClipWithCountdown() {
    if (!streamRef.current) {
      setCameraErr("Allow camera + mic first, then start the countdown.");
      return;
    }
    if (countdownRef.current) clearInterval(countdownRef.current);
    let remaining = 3;
    setCountdown(remaining);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(Math.max(remaining, 0));
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        startCameraClipCapture();
      }
    }, 1000);
  }

  function stopCameraClipCapture() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  function downloadCameraClip() {
    if (!cameraClip || !cameraClipUrl) return;
    const link = document.createElement("a");
    link.href = cameraClipUrl;
    link.download = "omnimark-camera-clip.webm";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function startPerformanceCapture() {
    if (!captureConsent) {
      setCameraErr("Confirm the performer has consented to AI performance transfer before recording.");
      return;
    }
    if (!performerName.trim()) {
      setCameraErr("Add the performer name or internal performer ID before recording.");
      return;
    }
    if (!window.MediaRecorder) {
      setCameraErr("This browser cannot record a performance capture. Use current Chrome, Edge, or Safari on a phone or laptop.");
      return;
    }
    try {
      let stream = streamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1080 }, height: { ideal: 1920 }, frameRate: { ideal: 30 } },
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraStatus("ready");
      }
      if (captureUrl) URL.revokeObjectURL(captureUrl);
      const chunks = [];
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm" });
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || "video/webm" });
        setCaptureBlob(blob);
        setCaptureUrl(URL.createObjectURL(blob));
        setCaptureStatus("ready");
        recorderRef.current = null;
      };
      recorderRef.current = recorder;
      recorder.start(1000);
      setCaptureStatus("recording");
      setCameraErr(null);
    } catch {
      setCaptureStatus("idle");
      setCameraErr("Could not start performance capture. Check camera and microphone permission, then try again.");
    }
  }

  function stopPerformanceCapture() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  function downloadPerformanceCapture() {
    if (!captureBlob || !captureUrl) return;
    const safeName = performerName.trim().replace(/[^a-z0-9]+/gi, "-").replace(/(^-|-$)/g, "").toLowerCase() || "performer";
    const link = document.createElement("a");
    link.href = captureUrl;
    link.download = `omnimark-${safeName}-performance-capture.webm`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  const results = output?.results || {};
  const resultKeys = Object.keys(results);
  
  // Find spokesperson output - could be at top level or nested in content
  const anySpokesperson = resultKeys.find((k) => {
    const r = results?.[k];
    return r?.spokesperson || r?.content?.meta?.spokesperson || r?.content?.spokesperson;
  }) || null;
  
  const getSpokesperson = (key) => {
    const r = results?.[key];
    return r?.spokesperson || r?.content?.meta?.spokesperson || r?.content?.spokesperson;
  };

  const getVariantScript = (sp) => {
    if (!sp) return "";
    if (typeof sp === "string") return sp;
    return (
      sp.script_15s ||
      sp.script_30s ||
      sp.script ||
      sp.video_script ||
      sp.body ||
      sp.text ||
      sp.copy ||
      sp.hook ||
      sp.message ||
      ""
    );
  };

  // Multi-presenter variants
  const variants = output?.spokesperson_variants || {};
  const variantPresenters = Object.keys(variants);
  const productionTools = [
    { name: "Shot list", desc: "Hero, close-up, proof, offer, CTA, and retargeting cuts." },
    { name: "Lighting board", desc: "Softbox, window light, neon retail, clean ecom, luxury studio." },
    { name: "Background set", desc: "White sweep, lifestyle room, desk setup, product table, green screen." },
    { name: "Script + teleprompter", desc: "Hook, demo, proof, objection, CTA, captions, and voice direction." },
    { name: "Retouch notes", desc: "Cleanup, color, crop, safe zones, thumbnails, and platform framing." },
    { name: "Export plan", desc: "9:16 reels, 1:1 feed, 16:9 web, landing hero, and campaign variants." },
  ];

  return (
    <div className="space-y-6" data-testid="studio-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">AI Studio</div>
          <div className="text-sm text-muted-foreground">
            Deploy your AI employees to create marketing packs in minutes.
          </div>
        </div>

        {workspace && (
          <div className="flex items-center gap-2">
            <select
              value={workspace.id}
              onChange={(e) => {
                const ws = workspaces.find((w) => w.id === e.target.value);
                if (ws) {
                  setWorkspace(ws);
                  setProduct("");
                  setNotes("");
                }
              }}
              className="rounded-md border px-2 py-1 text-sm"
              data-testid="workspace-select"
            >
              {workspaces.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </select>
            <Link to="/app/workspaces" className="text-sm text-muted-foreground hover:underline">
              manage
            </Link>
          </div>
        )}
      </div>

      {workspace && (
        <Card className="border-emerald-200 bg-emerald-50/60">
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-base">Workspace AI direction</CardTitle>
                <div className="text-sm text-muted-foreground">
                  Scripts, avatar videos, camera plans, and packs can follow this workspace or use a different direction.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUseWorkspaceContext((value) => !value)}
                className={[
                  "rounded-lg border px-3 py-2 text-sm transition",
                  useWorkspaceContext ? "bg-white font-medium" : "bg-transparent hover:bg-white/70",
                ].join(" ")}
              >
                {useWorkspaceContext ? "Using workspace" : "Workspace off"}
              </button>
            </div>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-[1fr_.9fr]">
            <div className="rounded-lg border bg-white p-3 text-sm">
              <div className="font-medium">{workspace.name}</div>
              <div className="mt-2 whitespace-pre-wrap text-muted-foreground">
                {workspaceBrief || "No Brand DNA saved yet. Add Brand DNA or use custom direction below."}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Choose something different</label>
              <Textarea
                value={customDirection}
                onChange={(e) => setCustomDirection(e.target.value)}
                placeholder="Example: Ignore normal brand tone for this video. Make it a bold TikTok-style product demo for parents, with a 3-second hook and soft CTA."
                className="min-h-[130px] bg-white"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProduct(brandDna?.offer || workspace.name);
                    setNotes(workspaceBrief);
                  }}
                >
                  Fill from workspace
                </Button>
                <Link to="/app/brand">
                  <Button type="button" variant="outline">Edit Brand DNA</Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle>AI Video Studio</CardTitle>
            <Badge variant="outline">{device.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[1.1fr_.9fr]">
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ["generate", "Generate without camera", "Use avatars, scripts, product notes, and AI packs."],
                ["camera", "Use camera + mic", "Record products, founder clips, UGC, and proofs."],
                ["performance", "Capture performer movement", "Record a consented human performance driver for OmniMotion."],
              ].map(([mode, label, desc]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setStudioMode(mode)}
                  className={[
                    "rounded-xl border p-3 text-left transition",
                    studioMode === mode ? "bg-muted ring-2 ring-primary" : "hover:bg-muted/60",
                  ].join(" ")}
                >
                  <div className="font-medium">{label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
                </button>
              ))}
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
              <div className="font-medium text-foreground">Device detection active</div>
              <div>{studioDeviceCopy}</div>
              <div className="mt-1 text-xs">
                Screen {device.width}x{device.height} • {device.orientation} • {device.touch ? "touch" : "mouse/trackpad"} • camera {device.cameraSupported ? "available" : "not available"}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {studioMode === "camera"
                ? "Use your phone camera and microphone to capture products, record talking-head clips, and give OmniMark AI footage for ads, dropshipping pages, and avatar-assisted edits."
                : studioMode === "performance"
                ? "Performance Capture is separate from Avatar Studio: record a rights-cleared real performer here. OmniMark will use the approved movement as a driver while your selected Avatar Studio identity and voice remain separate."
                : "Generate video-ready scripts, avatar presenter jobs, captions, product shot plans, and campaign cuts without opening the camera."}
            </div>
            <div className="flex flex-wrap gap-2">
              {["Product demo", "UGC review", "Founder intro", "Offer video", "Before/after", "Unboxing"].map((template) => (
                <Badge key={template} variant="outline">{template}</Badge>
              ))}
            </div>
            {studioMode === "camera" ? (
              <>
                {cameraErr && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{cameraErr}</div>}
                <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 text-sm">
                  <div className="font-medium">Camera-ready quality check</div>
                  <div className="mt-1 text-xs text-muted-foreground">Before recording: clean the lens, use even light, lock the phone vertically for short-form clips, and keep the subject or product fully inside the frame.</div>
                  {cameraQuality ? (
                    <div className="mt-2 rounded-lg border bg-white p-2 text-xs text-slate-700">
                      <div><span className="font-medium">Detected:</span> {cameraQuality.width || "unknown"}×{cameraQuality.height || "unknown"}{cameraQuality.frameRate ? ` at ${Math.round(cameraQuality.frameRate)} fps` : ""}</div>
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        {cameraQuality.recommendations.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  ) : <div className="mt-2 text-xs text-muted-foreground">Allow the camera to inspect the available capture settings on this device.</div>}
                </div>
                {cameraStatus === "ready" && (
                  <div className="grid gap-2 rounded-xl border bg-white p-3 text-sm sm:grid-cols-2">
                    <label className="grid gap-1">
                      <span className="text-xs font-medium">Camera</span>
                      <select
                        value={cameraDeviceId}
                        onChange={(event) => startMobileStudioCamera({ deviceId: event.target.value })}
                        className="rounded-md border bg-white px-2 py-2"
                      >
                        {cameraDevices.map((camera, index) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label || `Camera ${index + 1}`}</option>)}
                      </select>
                    </label>
                    <div className="grid gap-1">
                      <span className="text-xs font-medium">Microphone level</span>
                      <div className="flex h-10 items-center gap-2 rounded-md border px-2" aria-label={`Microphone level ${audioLevel}%`}>
                        <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${audioLevel}%` }} /></div>
                        <span className="w-9 text-right text-xs">{audioLevel}%</span>
                      </div>
                    </div>
                    <div className="sm:col-span-2 text-xs text-muted-foreground">The preview uses the full camera frame with “contain” sizing so OmniMark does not zoom or crop the person/product. The grid is a composition guide; recording stays on this device until you download it.</div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={cameraStatus === "ready" ? () => {} : requestStudioCamera} disabled={cameraStatus === "ready"} variant={cameraStatus === "ready" ? "outline" : "default"}>
                    {cameraStatus === "ready" ? "Camera ready" : device.supportsCameraStudio ? "Allow camera + mic" : "Test camera support"}
                  </Button>
                  {cameraStatus === "ready" && (
                    <Button onClick={stopMobileStudioCamera} variant="outline">Stop preview</Button>
                  )}
                  {cameraStatus === "ready" && cameraClipStatus !== "recording" && <Button onClick={() => startMobileStudioCamera({ facingMode: cameraFacing === "environment" ? "user" : "environment", deviceId: "" })} variant="outline">Switch front / rear</Button>}
                  {cameraStatus === "ready" && cameraClipStatus !== "recording" && !countdown && <Button onClick={startCameraClipWithCountdown}>Record in 3 seconds</Button>}
                  {cameraClipStatus === "recording" && <Button onClick={stopCameraClipCapture} variant="destructive">Stop recording</Button>}
                  {cameraClipStatus === "ready" && <Button onClick={downloadCameraClip} variant="outline">Download reviewed clip</Button>}
                  <Link to="/app/dropshipping">
                    <Button variant="outline">Use for dropshipping</Button>
                  </Link>
                </div>
                {cameraPermissionPending && (
                  <div role="dialog" aria-label="OmniMark camera and microphone permission" className="rounded-xl border border-sky-200 bg-white p-3 text-sm">
                    <div className="font-medium">OmniMark camera and microphone permission</div>
                    <div className="mt-1 text-xs text-muted-foreground">OmniMark will preview and record on this device only. It does not upload, publish, voice-clone, or charge anything when permission is granted.</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button onClick={() => { setCameraPermissionPending(false); startMobileStudioCamera(); }}>Continue to camera + mic permission</Button>
                      <Button variant="outline" onClick={() => setCameraPermissionPending(false)}>Not now</Button>
                    </div>
                  </div>
                )}
                {countdown > 0 && <Badge>Recording starts in {countdown}…</Badge>}
                {cameraClipStatus === "recording" && <Badge>Recording locally — review before sharing or using in a campaign</Badge>}
                {cameraClipStatus === "ready" && <div className="text-xs text-emerald-700">Camera clip is ready on this device with its original audio. It has not been uploaded, posted, voice-cloned, or charged.</div>}
              </>
            ) : studioMode === "performance" ? (
              <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                <div>
                  <div className="font-medium">Consented human performance capture</div>
                  <div className="mt-1 text-xs text-muted-foreground">Film one person, full body visible, stable framing, neutral background, no music, and one motion style per 10-second take.</div>
                </div>
                <Input value={performerName} onChange={(event) => setPerformerName(event.target.value)} placeholder="Performer name or internal ID" />
                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={captureConsent} onChange={(event) => setCaptureConsent(event.target.checked)} className="mt-0.5" />
                  <span>I confirm this performer has given written consent for OmniMark to use this recording for AI performance transfer and customer advertising outputs.</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={startMobileStudioCamera} variant="outline">Preview camera</Button>
                  {captureStatus !== "recording" ? (
                    <Button onClick={startPerformanceCapture}>Start consented capture</Button>
                  ) : (
                    <Button onClick={stopPerformanceCapture} variant="destructive">Stop capture</Button>
                  )}
                  {captureStatus === "ready" && <Button onClick={downloadPerformanceCapture} variant="outline">Download capture package</Button>}
                </div>
                {captureStatus === "recording" && <Badge>Recording performance driver</Badge>}
                {captureStatus === "ready" && <div className="text-xs text-emerald-700">Capture ready. Keep the downloaded file and signed release together; only an admin-approved driver can enter the performance library.</div>}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Link to="/app/studio/avatar">
                  <Button>Generate avatar video</Button>
                </Link>
                <Button onClick={() => setSpokespersonMode(true)} variant="outline">Build presenter scripts</Button>
                <Link to="/app/library">
                  <Button variant="outline">Open video library</Button>
                </Link>
              </div>
            )}
          </div>
          <div className="relative overflow-hidden rounded-lg border bg-black">
            {studioMode === "camera" || studioMode === "performance" ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="max-h-[70vh] w-full bg-black object-contain"
                />
                {cameraStatus === "ready" && (
                  <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3" aria-hidden="true">
                    {Array.from({ length: 9 }).map((_, index) => <div key={index} className="border border-white/20" />)}
                  </div>
                )}
                {cameraStatus !== "ready" && (
                  <div className="p-4 text-sm text-white/70">
                    {studioMode === "performance" ? "Full-body performer preview appears here after permission is granted." : "Camera preview appears here after permission is granted."}
                  </div>
                )}
              </>
            ) : (
              <div className="grid aspect-video place-items-center bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-4 text-white">
                <div className="max-w-sm text-center">
                  <div className="text-lg font-semibold">Generate without camera</div>
                  <div className="mt-2 text-sm text-white/70">
                    Choose an avatar, script, shot style, caption plan, and campaign destination. OmniMark keeps the video work inside the workspace.
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Production Toolkit</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          {productionTools.map((tool) => (
            <div key={tool.name} className="rounded-xl border bg-muted/30 p-3">
              <div className="font-medium">{tool.name}</div>
              <div className="mt-1 text-sm text-muted-foreground">{tool.desc}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {!workspace && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Select a workspace to use AI Studio.
            </div>
            <Link to="/app/workspaces">
              <Button>Go to Workspaces</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {workspace && (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Left: Pack selector + inputs */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Run a pack</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pack cards */}
              <div className="grid gap-3 md:grid-cols-3">
                {packs.map((p) => {
                  const active = p.key === packKey;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPackKey(p.key)}
                      className={[
                        "rounded-xl border p-3 text-left transition",
                        active ? "bg-muted ring-2 ring-primary" : "hover:bg-muted/60",
                      ].join(" ")}
                      data-testid={`pack-${p.key}`}
                    >
                      <div className="font-medium">{p.name}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{p.description}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.sequence.slice(0, 3).map((k) => (
                          <Badge key={k} variant="outline" className="text-[10px]">
                            {k.replace("_", " ")}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              <Separator />

              <div className="space-y-2">
                <label className="text-sm font-medium">Product or service</label>
                <Input
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="e.g., OmniMark HQ — AI marketing platform that deploys AI staff to create and run ads"
                  data-testid="product-input"
                />
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-2 md:col-span-1">
                  <label className="text-sm font-medium">Goal</label>
                  <div className="flex gap-2">
                    {["sales", "leads", "bookings"].map((g) => (
                      <button
                        key={g}
                        onClick={() => setGoal(g)}
                        className={[
                          "flex-1 rounded-lg border px-3 py-2 text-sm transition capitalize",
                          goal === g ? "bg-muted font-medium" : "hover:bg-muted/60",
                        ].join(" ")}
                        type="button"
                        data-testid={`goal-${g}`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Angles, promos, what to avoid, competitors, tone…"
                    className="min-h-[90px]"
                    data-testid="notes-input"
                  />
                </div>
              </div>

              <Separator />

              <div className="rounded-xl border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Spokesperson Variants</div>
                    <div className="text-sm text-muted-foreground">
                      Generate camera-ready scripts for multiple presenters.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSpokespersonMode((v) => !v)}
                    className={[
                      "rounded-lg border px-3 py-2 text-sm transition",
                      spokespersonMode ? "bg-muted font-medium" : "hover:bg-muted/60",
                    ].join(" ")}
                    data-testid="spokesperson-toggle"
                  >
                    {spokespersonMode ? "On" : "Off"}
                  </button>
                </div>

                {spokespersonMode && (
                  <>
                    {selectedAvatar && (
                      <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                        <div className="font-medium">Selected Omnimark avatar: {selectedAvatar.name}</div>
                        <div className="text-xs text-muted-foreground">
                          This avatar will guide presenter name, tone, and spokesperson variants.
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Presenters (up to 5)</label>
                      <div className="flex flex-wrap gap-2">
                        {people
                          .filter((p) => p.can_present)
                          .map((p) => {
                            const active = presenterKeys.includes(p.key);
                            return (
                              <button
                                key={p.key}
                                type="button"
                                onClick={() => {
                                  setPresenterKeys((prev) => {
                                    if (active) return prev.filter((x) => x !== p.key);
                                    if (prev.length >= 5) return prev;
                                    return [...prev, p.key];
                                  });
                                }}
                                className={[
                                  "rounded-lg border px-3 py-2 text-sm transition",
                                  active ? "bg-muted font-medium" : "hover:bg-muted/60",
                                ].join(" ")}
                                data-testid={`presenter-${p.key}`}
                              >
                                {p.name}
                              </button>
                            );
                          })}
                      </div>
                      <div className="text-xs text-muted-foreground">Selected: {presenterKeys.length}</div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Variants per presenter</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setVariantsPerPresenter(n)}
                              className={[
                                "flex-1 rounded-lg border px-3 py-2 text-sm transition",
                                variantsPerPresenter === n ? "bg-muted font-medium" : "hover:bg-muted/60",
                              ].join(" ")}
                              data-testid={`variants-${n}`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Presenter style (optional)</label>
                        <Input
                          value={presenterStyle}
                          onChange={(e) => setPresenterStyle(e.target.value)}
                          placeholder="premium, energetic, calm, bold…"
                          data-testid="presenter-style-input"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {estimate && (
                <div className="rounded-xl border p-3 space-y-1" data-testid="cost-estimate">
                  <div className="text-sm font-medium">Credit estimate</div>
                  <div className="text-xs text-muted-foreground">
                    Per call: {estimate.per_call_cost} • Base calls: {estimate.base_calls}
                    {estimate.variant_calls > 0 ? ` • Variant calls: ${estimate.variant_calls}` : ""}
                  </div>
                  <div className="text-sm">
                    Total calls: <span className="font-medium">{estimate.total_calls}</span> •
                    Estimated cost: <span className="font-medium">{estimate.total_cost}</span> credits
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button onClick={runPack} disabled={!ready || running} data-testid="run-pack-btn">
                  {running ? "Running pack…" : "Deploy AI Team"}
                </Button>

                <Link to="/app/brand">
                  <Button variant="outline">Review Brand DNA</Button>
                </Link>

                <Link to="/app/library" className="text-sm text-muted-foreground hover:underline">
                  View Library
                </Link>
              </div>

              {genId && (
                <div className="rounded-lg border bg-background p-3">
                  <div className="text-sm font-medium">Saved to Library</div>
                  <div className="text-xs text-muted-foreground">Generation ID: {genId}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Who will run */}
          <Card>
            <CardHeader>
              <CardTitle>Deployed team</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {selectedPack ? (
                <>
                  <div className="text-sm text-muted-foreground">{selectedPack.description}</div>
                  <Separator />
                  {selectedPack.sequence.map((k) => {
                    const p = people.find((x) => x.key === k);
                    return (
                      <div key={k} className="rounded-lg border p-3">
                        <div className="font-medium">{p?.name || k}</div>
                        <div className="text-sm text-muted-foreground">{p?.title}</div>
                        <div className="mt-2 text-xs text-muted-foreground">{p?.purpose}</div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Select a pack to see the team.</div>
              )}

              <Separator />
              <Link to="/app/upgrade">
                <Button variant="outline" className="w-full">
                  Upgrade / Buy Credits
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results */}
      {output && (
        <>
          {anySpokesperson && (
            <Card className="border-primary/50" data-testid="spokesperson-quick-view">
              <CardHeader>
                <CardTitle className="text-base">Spokesperson Output (Quick View)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="font-medium">Presenter:</span> {getSpokesperson(anySpokesperson)?.presenter}</div>
                <div><span className="font-medium">CTA:</span> {getSpokesperson(anySpokesperson)?.cta}</div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">30s Script</div>
                  <div className="whitespace-pre-wrap">{getSpokesperson(anySpokesperson)?.script_30s}</div>
                </div>
              </CardContent>
            </Card>
          )}

          {variantPresenters.length > 0 && (
            <Card data-testid="spokesperson-variants">
              <CardHeader>
                <CardTitle className="text-base">Spokesperson Variants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {variantPresenters.map((pk) => (
                  <div key={pk} className="rounded-xl border p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{pk.replace("_", " ")}</div>
                      <Badge variant="secondary">{variants[pk]?.length || 0} variants</Badge>
                    </div>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {(variants[pk] || []).map((v, idx) => {
                        // Handle nested structure: v.content.meta.spokesperson or v.spokesperson or v directly
                        const sp = v?.content?.meta?.spokesperson || v?.spokesperson || v;
                        const script = getVariantScript(sp);
                        return (
                          <div key={idx} className="rounded-lg border p-3">
                            <div className="text-xs text-muted-foreground mb-2">Variant {idx + 1}</div>
                            <div className="text-sm font-medium mb-2">{sp?.cta || "CTA"}</div>
                            <div className="text-sm whitespace-pre-wrap">
                              {script || "No script found."}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card data-testid="results-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Results
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(output, null, 2))}
                  >
                    Copy JSON
                  </Button>
                  <Link to="/app/library">
                    <Button variant="outline" size="sm">Open Library</Button>
                  </Link>
              </div>
            </CardTitle>
          </CardHeader>

          <CardContent>
            {resultKeys.length === 0 ? (
              <div className="text-sm text-muted-foreground">No results returned.</div>
            ) : (
              <Tabs defaultValue={resultKeys[0]} className="w-full">
                <TabsList className="flex flex-wrap">
                  {resultKeys.map((k) => (
                    <TabsTrigger key={k} value={k}>
                      {k.replace("_", " ")}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {resultKeys.map((k) => (
                  <TabsContent key={k} value={k} className="mt-4 space-y-3">
                    <div className="rounded-lg border bg-background p-3">
                      <pre className="max-h-[520px] overflow-auto text-xs leading-relaxed whitespace-pre-wrap">
                        {JSON.stringify(results[k], null, 2)}
                      </pre>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
}
